import { useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { type FormEvent, useEffect, useState } from "react";
import { LockIcon } from "../../components/icons";
import { OtpBadge, OtpInput, type OtpStatus } from "../../components/OtpInput";
import { authClient } from "../../lib/auth";
import { keys } from "../../lib/queries";
import { AuthField, AuthFrame, AuthLayout, AuthSteps, AuthSubmit, leaveAuth } from "./AuthShell";

const STEPS = ["Confirma tu contraseña", "Escanea el código", "Guarda los códigos de respaldo"];
const STEP_INDEX = { password: 0, scan: 1, done: 2 } as const;

/** The 32-character secret inside an otpauth:// URI, for manual entry. */
const secretOf = (uri: string) => new URL(uri).searchParams.get("secret") ?? "";

/**
 * Turns on two-step verification (TOTP). Owners and admins see this before the panel
 * when the environment requires it: password, scan the QR, confirm a code, save backup codes.
 */
export function TwoFactorSetup({ onCancel }: { onCancel?: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<"password" | "scan" | "done">("password");
  const [uri, setUri] = useState("");
  const [qr, setQr] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState<OtpStatus>("idle");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (uri) QRCode.toString(uri, { type: "svg", margin: 1, width: 200 }).then(setQr);
  }, [uri]);

  async function onPassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error } = await authClient.twoFactor.enable({
      password: String(new FormData(e.currentTarget).get("password")),
    });
    setBusy(false);
    if (error || !data) return setError("La contraseña no es correcta.");
    if (data.method !== "totp") return setError("No se pudo generar el código. Intenta de nuevo.");
    setUri(data.totpURI);
    setBackupCodes(data.backupCodes);
    setStep("scan");
  }

  async function verify(code: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setOtp("verifying");
    const { error } = await authClient.twoFactor.verifyTotp({ code });
    setBusy(false);
    if (error) {
      setOtp("error");
      return setError("El código no coincide. Revisa la hora del teléfono y prueba con el siguiente.");
    }
    // A beat to see the boxes turn green before the backup codes.
    setOtp("success");
    setTimeout(() => setStep("done"), 650);
  }

  function onCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    verify(String(new FormData(e.currentTarget).get("code")).replace(/\s/g, ""));
  }

  const brand = {
    side: "left" as const,
    title: "Protege tu cuenta",
    text: "Tu cuenta puede leer los chats y escribir desde el WhatsApp de la empresa. Además de la contraseña, vas a usar un código de tu teléfono.",
    extra: <AuthSteps steps={STEPS} current={STEP_INDEX[step]} />,
  };

  return (
    <AuthFrame brand={brand}>
      {step === "password" && (
        <AuthLayout title="Confirma tu contraseña" subtitle="Para activar la verificación en dos pasos.">
          <form onSubmit={onPassword}>
            <AuthField
              id="tf-password"
              name="password"
              type="password"
              label="Contraseña"
              icon={<LockIcon />}
              autoComplete="current-password"
              placeholder="Tu contraseña"
              required
            />
            {error && (
              <p className="error-text" role="alert">
                {error}
              </p>
            )}
            <AuthSubmit busy={busy}>{busy ? "Comprobando…" : "Continuar"}</AuthSubmit>
          </form>
          {onCancel && (
            <button className="auth-link" type="button" onClick={onCancel}>
              Cerrar sesión
            </button>
          )}
        </AuthLayout>
      )}

      {step === "scan" && (
        <AuthLayout
          title="Escanea el código"
          subtitle="Con una app de autenticación: Google Authenticator, Microsoft Authenticator o Authy."
        >
          <div className="auth-qr">
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: SVG generated locally by the qrcode library */}
            <div className="qr" dangerouslySetInnerHTML={{ __html: qr }} />
            <div className="auth-qr-help">
              <p>¿No puedes escanear? Escribe esta clave en la app:</p>
              <code className="secret mono">{secretOf(uri)}</code>
            </div>
          </div>
          <form onSubmit={onCode}>
            <div className="auth-field">
              <label htmlFor="tf-code">Código de 6 dígitos que muestra la app</label>
              <OtpInput
                id="tf-code"
                status={otp}
                onComplete={verify}
                onRetry={() => {
                  setOtp("idle");
                  setError(null);
                }}
              />
            </div>
            {error && (
              <p className="error-text" role="alert">
                {error}
              </p>
            )}
            <AuthSubmit busy={busy} disabled={otp === "success"}>
              {otp === "success" ? "Activada" : busy ? "Verificando…" : "Activar"}
            </AuthSubmit>
          </form>
          {onCancel && (
            <button className="auth-link" type="button" onClick={onCancel}>
              Cerrar sesión
            </button>
          )}
        </AuthLayout>
      )}

      {step === "done" && (
        <AuthLayout
          icon={<OtpBadge status="success" />}
          title="Verificación activa"
          subtitle="Guarda estos códigos de respaldo en un gestor de contraseñas o en un lugar seguro. Cada uno sirve una vez si pierdes el teléfono."
        >
          <ul className="auth-codes mono">
            {backupCodes.map((code) => (
              <li key={code}>{code}</li>
            ))}
          </ul>
          <button
            className="auth-secondary"
            type="button"
            onClick={() =>
              navigator.clipboard
                .writeText(backupCodes.join("\n"))
                .then(() => setCopied(true))
                .catch(() => {})
            }
          >
            {copied ? "Copiados" : "Copiar códigos"}
          </button>
          <button
            className="auth-submit"
            type="button"
            onClick={async () => {
              await leaveAuth();
              qc.invalidateQueries({ queryKey: keys.me });
            }}
          >
            Ya los guardé, entrar al panel
          </button>
        </AuthLayout>
      )}
    </AuthFrame>
  );
}
